import React from 'react';
import 'semantic-ui-css/components/modal.min.css';
import {
    Button,
    Checkbox,
    Dropdown,
    Icon,
    Input,
    Modal
} from 'semantic-ui-react';
import {
    createDefaultProductUnitDropdownOptions,
    isStandardProductUnit,
} from '../../../../shared/SharedResources';
import './product.list.filter-modal.scss';
import {
    COMPARE_OPTIONS,
    CompareOp,
    FILTER_FIELD_DROPDOWN_OPTIONS,
    FilterFieldKey,
    ProductFilterRow,
    TRI_STATE_OPTIONS,
    TriState,
    emptyFilterRow,
    fieldKind,
} from './product.list.filters';

/** Mount inside `.admin-layout-content` so the modal inherits admin theme variables and form styling. */
function getAdminModalMountNode(): HTMLElement | undefined {
    if (typeof document === 'undefined') {
        return undefined;
    }
    return document.getElementById('admin-modal-mount') ?? document.body;
}

function unitFilterValueOptions(currentText: string) {
    const standard = createDefaultProductUnitDropdownOptions();
    const t = currentText.trim();
    if (t !== '' && !isStandardProductUnit(t)) {
        return [...standard, { key: t, value: t, text: t }];
    }
    return standard;
}

type Props = {
    open: boolean;
    draftRows: ProductFilterRow[];
    onDraftChange: (rows: ProductFilterRow[]) => void;
    onClose: () => void;
    onApply: () => void;
};

function usedFieldsByOthers(rows: ProductFilterRow[], rowId: string): Set<FilterFieldKey> {
    const s = new Set<FilterFieldKey>();
    for (const r of rows) {
        if (r.id !== rowId && r.field) s.add(r.field as FilterFieldKey);
    }
    return s;
}

function fieldOptionsForRow(rows: ProductFilterRow[], row: ProductFilterRow) {
    const taken = usedFieldsByOthers(rows, row.id);
    return FILTER_FIELD_DROPDOWN_OPTIONS.filter((opt) => {
        if (opt.value === '') return true;
        if (row.field && opt.value === row.field) return true;
        return !taken.has(opt.value as FilterFieldKey);
    });
}

function updateRow(rows: ProductFilterRow[], id: string, patch: Partial<ProductFilterRow>): ProductFilterRow[] {
    return rows.map((r) => (r.id === id ? { ...r, ...patch } : r));
}

function onFieldSelect(rows: ProductFilterRow[], id: string, field: FilterFieldKey | ''): ProductFilterRow[] {
    return rows.map((r) => {
        if (r.id !== id) return r;
        return {
            ...r,
            field,
            text: '',
            op: '',
            value: '',
            tri: '',
            checked: false,
        };
    });
}

function renderConditionControls(
    row: ProductFilterRow,
    rows: ProductFilterRow[],
    onPatch: (id: string, patch: Partial<ProductFilterRow>) => void
) {
    if (!row.field) {
        return <span className="admin-product-filter-hint">Талбар сонгоно уу</span>;
    }
    const kind = fieldKind(row.field as FilterFieldKey);
    if (!kind) return null;

    if (kind === 'text') {
        if (row.field === 'unit') {
            return (
                <Dropdown
                    fluid
                    selection
                    clearable
                    placeholder="Нэгж сонгох"
                    options={unitFilterValueOptions(row.text)}
                    value={row.text}
                    onChange={(e, { value }) => onPatch(row.id, { text: String(value ?? '') })}
                />
            );
        }
        return (
            <Input
                fluid
                placeholder="Текст хайх"
                value={row.text}
                onChange={(e) => onPatch(row.id, { text: e.target.value })}
            />
        );
    }
    if (kind === 'numeric') {
        return (
            <div className="admin-product-filter-numeric-row">
                <Dropdown
                    selection
                    placeholder="Харьцуулалт"
                    options={COMPARE_OPTIONS}
                    value={row.op}
                    onChange={(e, { value }) => onPatch(row.id, { op: value as CompareOp })}
                    className="admin-product-filter-compare-dd"
                />
                <Input
                    type="number"
                    placeholder="Утга"
                    value={row.value}
                    onChange={(e) => onPatch(row.id, { value: e.target.value })}
                    className="admin-product-filter-numeric-input"
                />
            </div>
        );
    }
    if (kind === 'tri') {
        return (
            <Dropdown
                fluid
                selection
                placeholder="Сонгох"
                options={TRI_STATE_OPTIONS}
                value={row.tri}
                onChange={(e, { value }) => onPatch(row.id, { tri: value as TriState })}
            />
        );
    }
    if (kind === 'bool') {
        return (
            <Checkbox
                label="Тийм"
                checked={row.checked}
                onChange={(e, { checked }) => onPatch(row.id, { checked: !!checked })}
            />
        );
    }
    return null;
}

export default function ProductListFilterModal({ open, draftRows, onDraftChange, onClose, onApply }: Props) {
    const onPatch = (id: string, patch: Partial<ProductFilterRow>) => {
        onDraftChange(updateRow(draftRows, id, patch));
    };

    const removeRow = (id: string) => {
        onDraftChange(draftRows.filter((r) => r.id !== id));
    };

    const addRow = () => {
        onDraftChange([...draftRows, emptyFilterRow()]);
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            size="large"
            closeOnDimmerClick={false}
            className="admin-product-filter-modal"
            mountNode={getAdminModalMountNode()}
        >
            <Modal.Header>
                <Icon name="filter" /> Нарийвчилсан филтер
            </Modal.Header>
            <Modal.Content>
                {draftRows.length === 0 ? (
                    null
                ) : (
                    draftRows.map((row, index) => (
                        <div key={row.id} className="admin-product-filter-row">
                            <div>
                                <div className="admin-product-filter-row-label">Талбар</div>
                                <Dropdown
                                    fluid
                                    selection
                                    options={fieldOptionsForRow(draftRows, row)}
                                    value={row.field}
                                    onChange={(e, { value }) =>
                                        onDraftChange(onFieldSelect(draftRows, row.id, value as FilterFieldKey | ''))
                                    }
                                />
                            </div>
                            <div>
                                <div className="admin-product-filter-row-label">Нөхцөл {index + 1}</div>
                                {renderConditionControls(row, draftRows, onPatch)}
                            </div>
                            <div className="admin-product-filter-remove-col">
                                <div className="admin-product-filter-row-label">Устгах</div>
                                <Button
                                    type="button"
                                    icon
                                    basic
                                    aria-label="Мөр устгах"
                                    onClick={() => removeRow(row.id)}
                                    className="admin-product-filter-remove-btn"
                                >
                                    <Icon name="trash" />
                                </Button>
                            </div>
                        </div>
                    ))
                )}
                <Button
                    type="button"
                    basic
                    icon
                    labelPosition="left"
                    onClick={addRow}
                    className="admin-product-filter-add-row-btn"
                >
                    <Icon name="plus" />
                    Мөр нэмэх
                </Button>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose}>Болих</Button>
                <Button primary onClick={onApply}>
                    Хэрэглэх
                </Button>
            </Modal.Actions>
        </Modal>
    );
}
